import { useEffect, useRef, useState } from 'react';

import { useDebounce } from '@hooks/useDebounce';
import { MIN_SEARCH_CHARS, SEARCH_DEBOUNCE_MS } from '@src/constants/constants';

interface UseDebouncedSearchOptions {
  minChars?: number;
  debounceMs?: number;
}

interface UseDebouncedSearchResult {
  // Debounce timer is running (term is above threshold but hasn't settled
  // yet) — does not reflect the caller's own query loading state.
  isPending: boolean;
  // True once a search has fired and settled for the current above-threshold
  // term. Resets to false whenever the term drops back below minChars, and
  // also while a newer term is still debouncing (not yet settled) — so a
  // stale "no results" from an older search can't outlive a newer search's
  // pending window, even for a caller that doesn't separately check
  // isPending.
  hasSearched: boolean;
  isBelowThreshold: boolean;
  // The debounced term once settled and above threshold, otherwise ''.
  // Lets a caller with a second term-keyed query/hook (e.g. a paginated
  // list alongside the primary search) derive it from this single gated
  // value instead of re-implementing its own debounce/threshold (TA-10166).
  gatedTerm: string;
}

/**
 * Gates and debounces an API-backed search. Query-agnostic: callers pass
 * their own search executor (an Apollo lazy query call, or anything else)
 * rather than the hook owning a specific query. Callers merge `isPending`
 * with their own query's `loading` for the full-duration spinner, since this
 * hook only tracks the debounce window, not the request itself (TA-10091).
 */
export const useDebouncedSearch = (
  term: string,
  onSearch: (term: string) => void | Promise<unknown>,
  {
    minChars = MIN_SEARCH_CHARS,
    debounceMs = SEARCH_DEBOUNCE_MS,
  }: UseDebouncedSearchOptions = {}
): UseDebouncedSearchResult => {
  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  const trimmed = term.trim();
  const isBelowThreshold = trimmed.length < minChars;
  const debouncedTerm = useDebounce(trimmed, debounceMs);
  // Whether the debounced value has actually caught up to the live term —
  // required in addition to isBelowThreshold because isBelowThreshold is
  // derived from the live term and can flip before debouncedTerm catches up,
  // which would otherwise fire onSearch with a stale, pre-threshold value.
  const settled = debouncedTerm === trimmed;
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    if (isBelowThreshold || !settled) {
      setHasSearched(false);
      return;
    }
    // Swallow an onSearch failure here — this hook doesn't own the caller's
    // error state (that's the caller's own query loading/error to handle).
    // The try/catch is required in addition to .catch(): calling
    // onSearchRef.current(debouncedTerm) as Promise.resolve's argument means
    // a synchronous throw happens before Promise.resolve is even reached, so
    // .catch() alone only handles an async rejection, not a sync throw.
    try {
      Promise.resolve(onSearchRef.current(debouncedTerm)).catch(() => {});
    } catch {
      // Swallow a synchronous throw too.
    }
    setHasSearched(true);
    // onSearch is read from a ref so callers don't need to memoize it to
    // avoid re-firing on every render — only debouncedTerm/settled/
    // isBelowThreshold should trigger a fire.
  }, [debouncedTerm, settled, isBelowThreshold]);

  return {
    isPending: !isBelowThreshold && !settled,
    hasSearched,
    isBelowThreshold,
    gatedTerm: !isBelowThreshold && settled ? debouncedTerm : '',
  };
};
